import { Input } from "@/shared/ui/input";
import type { CategoryFormData } from "../hooks/useCategoryFormData";
import { IconSelector } from "./IconSelector";

interface CategoryFormFieldsProps {
  formData: CategoryFormData;
  onFieldChange: (updates: Partial<CategoryFormData>) => void;
  isLoading: boolean;
  isSystem?: boolean;
}

export function CategoryFormFields({
  formData,
  onFieldChange,
  isLoading,
  isSystem = false,
}: CategoryFormFieldsProps) {
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    // For non-system categories, auto-sync displayName if it hasn't been
    // explicitly customized (i.e. it matches the current name or is empty)
    if (!isSystem && (!formData.displayName || formData.displayName === formData.name)) {
      onFieldChange({ name: newName, displayName: newName });
    } else {
      onFieldChange({ name: newName });
    }
  };

  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <label htmlFor="name" className="text-sm font-medium text-ink-900">
          Category Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="name"
          value={formData.name}
          onChange={handleNameChange}
          placeholder="Enter category name"
          required
          disabled={isLoading || isSystem}
        />
        {isSystem && (
          <p className="text-xs text-ink-500">
            System category names cannot be changed.
          </p>
        )}
      </div>
      <div className="space-y-2">
        <label htmlFor="displayName" className="text-sm font-medium text-ink-900">
          Display Name
        </label>
        <Input
          id="displayName"
          value={formData.displayName}
          onChange={(e) => onFieldChange({ displayName: e.target.value })}
          placeholder={formData.name || "Enter display name"}
          disabled={isLoading}
        />
        <p className="text-xs text-ink-500">
          Optional custom name shown to users. Defaults to category name if empty.
        </p>
      </div>
      <div className="space-y-2">
        <label htmlFor="icon" className="text-sm font-medium text-ink-900">
          Icon
        </label>
        <IconSelector
          value={formData.icon}
          onChange={(icon) => onFieldChange({ icon })}
          disabled={isLoading}
        />
      </div>
    </div>
  );
}

